import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { Lightbox } from "@/pages/material/Lightbox";
import { useMaMediaContextMenu } from "@/pages/material/useMaMediaContextMenu";
import { imageElementFromContextEvent } from "@/lib/clipboardMedia";
import {
  formatMaPositionOrderSummary,
  fmtMaDateTime,
  maBearbeiterDisplayName,
  MaFortschrittBadge,
  maLieferterminSummary,
  MaPositionOrderBadge,
  maPositionOrderState,
  maPositionOrderSummary,
} from "@/pages/material/maListDisplay";
import {
  assignMaterialAnforderung,
  claimMaterialAnforderung,
  clearMaPositionDisposition,
  convertMaToAnfrage,
  convertMaToOrder,
  disposeMaPositions,
  getMaAttachmentThumbUrl,
  getMaAttachmentUrl,
  linkMaOrder,
  markMaBereitgelegt,
  openMaterialAnforderungLagerPdf,
  patchMaterialAnforderung,
  releaseMaterialAnforderung,
  stornierenMaterialAnforderung,
  unlinkMaOrder,
  unmarkMaBereitgelegt,
  type MaDetail,
  type MaLagerPdfOnly,
  type MaPositionDisposition,
} from "@/lib/material";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { getUserLocalLogin, useCurrentUser, userHasGroup, userIsBuero } from "@/lib/auth";
import { getOrder, getOrderNotes, type ProjectNote } from "@/lib/projectOverview";
import type { Order } from "@/lib/projects";
import { NotesSection } from "@/projectOverview/sections/NotesSection";
import { MaOrderSupplierDialog } from "@/pages/material/MaOrderSupplierDialog";
import { MaBereitgelegtDialog } from "@/pages/material/MaBereitgelegtDialog";
import { MaPositionDispositionDialog } from "@/pages/material/MaPositionDispositionDialog";
import { MaLinkOrderDialog } from "@/pages/material/MaLinkOrderDialog";
import { MaStornoDialog } from "@/pages/material/MaStornoDialog";
import { MaBearbeitungTransferDialog } from "@/pages/material/MaBearbeitungTransferDialog";
import { useOrderWizard } from "@/pages/bestellungen/useOrderWizard";
import { useAnfrageWizard } from "@/pages/anfragen/useAnfrageWizard";
import type { OrderSupplierState } from "@/lib/orderDraftUtils";

export interface MaBueroPanelProps {
  detail: MaDetail;
  onClose: () => void;
  onChanged: () => void;
  onReload: () => void;
}

function FortschrittBadge({
  f,
  status,
}: {
  f: MaDetail["monteur_fortschritt"];
  status: MaDetail["status"];
}) {
  return <MaFortschrittBadge f={f} status={status} />;
}

function loginMatchesAuthor(userLogin: string, author: string | null | undefined): boolean {
  const a = userLogin.trim().toLowerCase();
  const b = (author || "").trim().toLowerCase();
  if (!a || !b) return false;
  return a === b || b.startsWith(`${a}@`);
}

export function MaBueroPanel({
  detail,
  onClose,
  onChanged,
  onReload,
}: MaBueroPanelProps) {
  const { user } = useCurrentUser();
  const isBuero = userIsBuero(user);
  const isAdmin = userHasGroup(user, "App-Admins");
  const { openDraftById: openOrderDraftById } = useOrderWizard();
  const { openDraftById: openAnfrageDraftById } = useAnfrageWizard();

  const [bueroNotizen, setBueroNotizen] = useState(detail.buero_notizen ?? "");
  const [saving, setSaving] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [takeoverConfirm, setTakeoverConfirm] = useState(false);
  const [busy, setBusy] = useState<null | "anfrage" | "order" | "link">(null);
  const [orderSupplierOpen, setOrderSupplierOpen] = useState(false);
  const [bereitgelegtOpen, setBereitgelegtOpen] = useState(false);
  const [dispositionOpen, setDispositionOpen] = useState(false);
  const [dispositionMode, setDispositionMode] =
    useState<MaPositionDisposition>("storniert");
  const [dispositionInitialIds, setDispositionInitialIds] = useState<number[]>([]);
  const [dispositionLoading, setDispositionLoading] = useState(false);
  const [linkOrderOpen, setLinkOrderOpen] = useState(false);
  const [replaceOrderId, setReplaceOrderId] = useState<number | null>(null);
  const [linkPositionIds, setLinkPositionIds] = useState<number[] | null>(null);
  const [stornoOpen, setStornoOpen] = useState(false);
  const [stornoLoading, setStornoLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferMode, setTransferMode] = useState<"release" | "assign">("release");
  const [transferLoading, setTransferLoading] = useState(false);
  const [bereitgelegtLoading, setBereitgelegtLoading] = useState(false);
  const [printOnly, setPrintOnly] = useState<MaLagerPdfOnly>("bereitgelegt");
  const [printLoading, setPrintLoading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxCopySource, setLightboxCopySource] = useState<{
    url: string;
    filename: string;
    contentType: string;
  } | null>(null);
  const { openMediaContextMenu, contextMenuNode } = useMaMediaContextMenu();

  const [project, setProject] = useState<Order | null>(null);
  const [projectNotes, setProjectNotes] = useState<ProjectNote[]>([]);

  useEffect(() => {
    setBueroNotizen("");
  }, [detail.id]);

  const orderDrafts = detail.linked_order_drafts ?? [];
  const linkedAga = detail.linked_aga;
  const hasLinkedItems =
    detail.linked_anfragen.length > 0 ||
    detail.linked_orders.length > 0 ||
    orderDrafts.length > 0 ||
    !!linkedAga;
  const showLinkedSection = hasLinkedItems || isBuero || isAdmin;

  const reloadProjectNotes = useCallback(async () => {
    if (!detail.auftrag_id) {
      setProject(null);
      setProjectNotes([]);
      return;
    }
    try {
      const [p, notes] = await Promise.all([
        getOrder(detail.auftrag_id),
        getOrderNotes(detail.auftrag_id),
      ]);
      setProject(p);
      setProjectNotes(notes);
    } catch {
      setProject(null);
      setProjectNotes([]);
    }
  }, [detail.auftrag_id]);

  useEffect(() => {
    void reloadProjectNotes();
  }, [reloadProjectNotes]);

  const fortschritt = detail.monteur_fortschritt;
  const bearbeiterName = maBearbeiterDisplayName(detail);
  const takeoverFromName =
    (detail.takeover_from_name || detail.takeover_from || "").trim() || null;
  const closed = detail.status === "erledigt" || detail.status === "abgelehnt";
  const myLogin = getUserLocalLogin(user);
  const claimedByOther = Boolean(
    detail.bearbeitet_von && !loginMatchesAuthor(myLogin, detail.bearbeitet_von),
  );
  const claimedByMe = Boolean(
    detail.bearbeitet_von && loginMatchesAuthor(myLogin, detail.bearbeitet_von),
  );
  const canActOnMa = claimedByMe && !closed;
  const canTransferMa = (claimedByMe || (claimedByOther && isAdmin)) && !closed && Boolean(detail.bearbeitet_von);
  const canEditBueroNotizen = !claimedByOther && !closed;
  const isBereitgelegt =
    Boolean(detail.bereitgelegt_at) || detail.monteur_fortschritt === "bereitgelegt";
  const hasPartialBereitgelegt = (detail.positionen || []).some((p) =>
    Boolean(p.bereitgelegt_at),
  );
  const hasOpenForLager = (detail.positionen || []).some((p) => {
    const state = maPositionOrderState(p);
    return state === "offen" || state === "entwurf";
  });
  const isGeliefert = detail.monteur_fortschritt === "geliefert";
  const canMarkBereitgelegt =
    canActOnMa &&
    !isGeliefert &&
    ((detail.positionen || []).length ? hasOpenForLager : !isBereitgelegt);
  const canUnmarkBereitgelegt =
    claimedByMe && !isGeliefert && (isBereitgelegt || hasPartialBereitgelegt);
  const hasDisposablePositions = (detail.positionen || []).some((p) => {
    const state = maPositionOrderState(p);
    return (
      state !== "bestellt" &&
      state !== "geliefert" &&
      state !== "storniert" &&
      state !== "abgelehnt"
    );
  });
  const canDisposePositions = canActOnMa && hasDisposablePositions;
  const remainingOpenIds = (detail.positionen || [])
    .filter((p) => {
      const state = maPositionOrderState(p);
      return (state === "offen" || state === "entwurf") && p.id != null;
    })
    .map((p) => p.id);
  const positionSummary = maPositionOrderSummary(detail.positionen);
  const canPrintSummary =
    (isBuero || isAdmin) &&
    (printOnly === "all"
      ? positionSummary.total > 0 && Boolean(detail.bearbeitet_von)
      : positionSummary.bereitgelegt > 0);
  const canPrintMaSheet =
    (isBuero || isAdmin) &&
    Boolean(detail.bearbeitet_von) &&
    detail.status !== "abgelehnt";

  const handlePrintSummary = useCallback(async () => {
    if (!canPrintSummary) return;
    setPrintLoading(true);
    try {
      await openMaterialAnforderungLagerPdf(detail.id, printOnly);
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Zusammenfassung konnte nicht geladen werden.",
      );
    } finally {
      setPrintLoading(false);
    }
  }, [canPrintSummary, detail.id, printOnly]);

  const handlePrintMaSheet = useCallback(async () => {
    if (!canPrintMaSheet) return;
    setPrintLoading(true);
    try {
      await openMaterialAnforderungLagerPdf(detail.id, "all");
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Materialanforderung konnte nicht gedruckt werden.",
      );
    } finally {
      setPrintLoading(false);
    }
  }, [canPrintMaSheet, detail.id]);

  const saveBueroNotizen = useCallback(async () => {
    const text = bueroNotizen.trim();
    if (!text) {
      toast.warning("Bitte zuerst eine Notiz eingeben.");
      return;
    }
    setSaving(true);
    try {
      await patchMaterialAnforderung(detail.id, {
        buero_notizen: text,
      });
      setBueroNotizen("");
      onChanged();
      onReload();
      await reloadProjectNotes();
      toast.success("Notiz gespeichert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }, [bueroNotizen, detail.id, onChanged, onReload, reloadProjectNotes]);

  async function handleClaim() {
    setClaiming(true);
    try {
      const res = await claimMaterialAnforderung(detail.id);
      onChanged();
      onReload();
      await reloadProjectNotes();
      setTakeoverConfirm(false);
      if (res.takeover_from) {
        const prev =
          (res.takeover_from_name || res.takeover_from || "").trim() || res.takeover_from;
        toast.success(`Bearbeitung übernommen (zuvor: ${prev}).`);
      } else {
        toast.success("Bearbeitung gestartet.");
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Bearbeitung konnte nicht übernommen werden.",
      );
    } finally {
      setClaiming(false);
    }
  }

  async function handleAnfrage() {
    setBusy("anfrage");
    try {
      const res = await convertMaToAnfrage(detail.id);
      onChanged();
      onReload();
      if (res.anfrage_id) {
        await openAnfrageDraftById(res.anfrage_id, {
          sourceMaId: detail.id,
          title: `Anfrage aus ${detail.ma_nummer}`,
        });
        onClose();
      } else {
        toast.error("TA-Entwurf konnte nicht erstellt werden.");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Konvertieren fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  async function handleOrderConfirm({
    supplier,
    positionIds,
  }: {
    supplier: OrderSupplierState;
    positionIds: number[];
  }) {
    setBusy("order");
    try {
      const contactName =
        (supplier.person || "").trim() ||
        [supplier.contact_first_name, supplier.contact_last_name]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        null;
      const res = await convertMaToOrder(detail.id, {
        supplier_id: supplier.id!,
        supplier_name: supplier.name || null,
        supplier_contact_first_name: supplier.contact_first_name || null,
        supplier_contact_last_name: supplier.contact_last_name || null,
        supplier_contact_anrede: supplier.anrede || null,
        supplier_contact_name: contactName,
        supplier_email: supplier.email || null,
        position_ids: positionIds,
      });
      onChanged();
      onReload();
      const draftId = res.order_draft_id ?? res.order_id;
      if (draftId) {
        setOrderSupplierOpen(false);
        await openOrderDraftById(draftId, `Bestellung aus ${detail.ma_nummer}`);
        onClose();
      } else {
        toast.error("Entwurf konnte nicht erstellt werden.");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Konvertieren fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  function openLinkOrderDialog(
    replaceId: number | null = null,
    positionIds: number[] | null = null,
  ) {
    setReplaceOrderId(replaceId);
    setLinkPositionIds(positionIds);
    setLinkOrderOpen(true);
  }

  async function handleLinkOrderConfirm(orderId: number) {
    setBusy("link");
    try {
      const previousReplaceId = replaceOrderId;
      const result = await linkMaOrder(
        detail.id,
        orderId,
        linkPositionIds ?? undefined,
      );
      if (previousReplaceId != null && previousReplaceId !== orderId) {
        try {
          await unlinkMaOrder(detail.id, previousReplaceId);
        } catch (unlinkErr) {
          toast.warning(
            unlinkErr instanceof ApiError
              ? `Neue Bestellung verknüpft, alte Verknüpfung konnte nicht getrennt werden: ${unlinkErr.message}`
              : "Neue Bestellung verknüpft, alte Verknüpfung konnte nicht getrennt werden.",
          );
          setLinkOrderOpen(false);
          setReplaceOrderId(null);
          setLinkPositionIds(null);
          onChanged();
          onReload();
          return;
        }
      }
      setLinkOrderOpen(false);
      setReplaceOrderId(null);
      setLinkPositionIds(null);
      onChanged();
      onReload();
      const attached = result.attached_position_ids?.length ?? 0;
      toast.success(
        previousReplaceId != null
          ? "Bestellung geändert."
          : attached > 0
            ? attached === 1
              ? "Position mit der Bestellung verknüpft."
              : `${attached} Positionen mit der Bestellung verknüpft.`
            : "Bestellung verknüpft.",
      );
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Verknüpfung fehlgeschlagen.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function handleAttachOpenPositions(orderId: number, label: string) {
    const ids = remainingOpenIds;
    if (!ids.length) {
      toast.info("Keine offenen Positionen mehr.");
      return;
    }
    setBusy("link");
    try {
      const result = await linkMaOrder(detail.id, orderId, ids);
      onChanged();
      onReload();
      const attached = result.attached_position_ids?.length ?? 0;
      toast.success(
        attached === 1
          ? `Position mit ${label} verknüpft.`
          : `${attached} Positionen mit ${label} verknüpft.`,
      );
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Verknüpfung fehlgeschlagen.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function handleUnlinkOrder(orderId: number, label: string) {
    if (
      !confirm(
        `Verknüpfung zu Bestellung „${label}“ wirklich aufheben?`,
      )
    ) {
      return;
    }
    setBusy("link");
    try {
      await unlinkMaOrder(detail.id, orderId);
      onChanged();
      onReload();
      toast.success("Verknüpfung aufgehoben.");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Trennung fehlgeschlagen.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function handleStorno(grund: string) {
    setStornoLoading(true);
    try {
      await stornierenMaterialAnforderung(detail.id, grund);
      setStornoOpen(false);
      onChanged();
      onReload();
      await reloadProjectNotes();
      toast.success("Materialanforderung storniert.");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Stornierung fehlgeschlagen.",
      );
    } finally {
      setStornoLoading(false);
    }
  }

  function openTransfer(mode: "release" | "assign") {
    setTransferMode(mode);
    setTransferOpen(true);
  }

  async function handleRelease(grund: string) {
    setTransferLoading(true);
    try {
      await releaseMaterialAnforderung(detail.id, grund);
      setTransferOpen(false);
      onChanged();
      onReload();
      await reloadProjectNotes();
      toast.success("Bearbeitung freigegeben.");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Freigabe fehlgeschlagen.",
      );
    } finally {
      setTransferLoading(false);
    }
  }

  async function handleAssign(assigneeLogin: string, grund: string) {
    setTransferLoading(true);
    try {
      const res = await assignMaterialAnforderung(detail.id, {
        assignee_login: assigneeLogin,
        grund,
      });
      setTransferOpen(false);
      onChanged();
      onReload();
      await reloadProjectNotes();
      if (res.takeover_from_name) {
        toast.success(`An ${res.bearbeitet_von_name || assigneeLogin} zugewiesen.`);
      } else {
        toast.success("Bearbeitung zugewiesen.");
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Zuweisung fehlgeschlagen.",
      );
    } finally {
      setTransferLoading(false);
    }
  }

  function openBereitgelegtDialog() {
    if (!(detail.positionen || []).length) {
      void handleMarkBereitgelegtConfirm(undefined);
      return;
    }
    setBereitgelegtOpen(true);
  }

  async function handleMarkBereitgelegtConfirm(positionIds: number[] | undefined) {
    setBereitgelegtLoading(true);
    try {
      const res = await markMaBereitgelegt(
        detail.id,
        positionIds != null ? { position_ids: positionIds } : {},
      );
      setBereitgelegtOpen(false);
      onChanged();
      onReload();
      await reloadProjectNotes();
      if (res.already_marked) {
        toast.success("War bereits als im Lager bereitgelegt markiert.");
      } else if (res.partial) {
        const n = res.marked_position_ids?.length ?? positionIds?.length ?? 0;
        toast.success(
          n === 1
            ? "1 Position im Lager bereitgelegt. Die übrigen können bestellt werden."
            : `${n} Positionen im Lager bereitgelegt. Die übrigen können bestellt werden.`,
        );
      } else {
        toast.success("Material als im Lager bereitgelegt markiert.");
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Bereitstellung konnte nicht markiert werden.",
      );
    } finally {
      setBereitgelegtLoading(false);
    }
  }

  async function handleUnmarkBereitgelegt() {
    setBereitgelegtLoading(true);
    try {
      await unmarkMaBereitgelegt(detail.id);
      onChanged();
      onReload();
      await reloadProjectNotes();
      toast.success("Lager-Bereitstellung zurückgenommen.");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Zurücknahme fehlgeschlagen.",
      );
    } finally {
      setBereitgelegtLoading(false);
    }
  }

  function openDispositionDialog(
    mode: MaPositionDisposition,
    positionIds: number[] = [],
  ) {
    setDispositionMode(mode);
    setDispositionInitialIds(positionIds);
    setDispositionOpen(true);
  }

  async function handleDispositionConfirm(positionIds: number[], grund: string) {
    setDispositionLoading(true);
    try {
      await disposeMaPositions(detail.id, {
        status: dispositionMode,
        grund,
        position_ids: positionIds,
      });
      setDispositionOpen(false);
      onChanged();
      onReload();
      await reloadProjectNotes();
      const n = positionIds.length;
      toast.success(
        dispositionMode === "storniert"
          ? n === 1
            ? "1 Position storniert."
            : `${n} Positionen storniert.`
          : n === 1
            ? "1 Position abgelehnt."
            : `${n} Positionen abgelehnt.`,
      );
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Positionen konnten nicht aktualisiert werden.",
      );
    } finally {
      setDispositionLoading(false);
    }
  }

  async function handleClearDisposition(positionId: number) {
    setDispositionLoading(true);
    try {
      await clearMaPositionDisposition(detail.id, { position_ids: [positionId] });
      onChanged();
      onReload();
      await reloadProjectNotes();
      toast.success("Positionsstatus zurückgenommen.");
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Zurücknahme fehlgeschlagen.",
      );
    } finally {
      setDispositionLoading(false);
    }
  }

  const storedAttachments = (detail.attachments || []).filter((a) => a.position_id == null);
  const showProjectNotes = detail.auftrag_id && project;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden p-1">
          <div className="min-w-0 space-y-4">
            <section className="min-w-0 rounded-ios border border-separator bg-card-elevated p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                Büro-Bearbeitung
              </p>

              {claimedByMe ? (
                <div
                  className="mt-2 rounded-lg border border-status-green/35 bg-status-green/10 px-3 py-2.5"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-status-green">
                    Sie bearbeiten diese Anforderung
                  </p>
                  <p className="mt-1 text-sm font-semibold text-label">{bearbeiterName}</p>
                  {detail.bearbeitet_at ? (
                    <p className="text-xs text-label-secondary">
                      Übernommen {fmtMaDateTime(detail.bearbeitet_at)}
                    </p>
                  ) : null}
                  {takeoverFromName ? (
                    <p className="mt-1 text-xs text-label-secondary">
                      Übernahme von {takeoverFromName}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <FortschrittBadge f={fortschritt} status={detail.status} />
                    {maLieferterminSummary(detail) ? (
                      <span className="text-sm text-label-secondary">
                        LT {maLieferterminSummary(detail)}
                      </span>
                    ) : null}
                    {canActOnMa && detail.auftrag_id ? (
                      <ProjectOverviewButton auftragId={detail.auftrag_id} />
                    ) : null}
                  </div>
                  {canTransferMa ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTransfer("assign")}
                      >
                        Kollegen zuweisen
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTransfer("release")}
                      >
                        Bearbeitung freigeben
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : claimedByOther ? (
                <div
                  className="mt-2 rounded-lg border border-status-orange/35 bg-status-orange/10 px-3 py-2.5"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-status-orange">
                    In Bearbeitung durch
                  </p>
                  <p className="mt-1 text-base font-semibold text-label">{bearbeiterName}</p>
                  {detail.bearbeitet_at ? (
                    <p className="text-xs text-label-secondary">
                      Übernommen {fmtMaDateTime(detail.bearbeitet_at)}
                    </p>
                  ) : null}
                  {!closed ? (
                    takeoverConfirm ? (
                      <div className="mt-3 rounded-lg border border-separator bg-card p-3">
                        <p className="text-sm text-label">
                          Die Bearbeitung von{" "}
                          <span className="font-semibold">{bearbeiterName}</span> übernehmen?
                          Die Person verliert damit die Bearbeitungsrechte für TA/TK und
                          Projektnotizen.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setTakeoverConfirm(false)}
                          >
                            Abbrechen
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            loading={claiming}
                            onClick={() => void handleClaim()}
                          >
                            Übernahme bestätigen
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="mt-2 text-xs text-accent hover:underline"
                        onClick={() => setTakeoverConfirm(true)}
                      >
                        Bearbeitung von {bearbeiterName} übernehmen …
                      </button>
                    )
                  ) : null}
                  {canTransferMa && !claimedByMe ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTransfer("assign")}
                      >
                        Kollegen zuweisen
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTransfer("release")}
                      >
                        Bearbeitung freigeben
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : !closed ? (
                <div className="mt-2 flex min-w-0 flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-label-secondary">
                      Noch nicht vom Büro übernommen.
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <FortschrittBadge f={fortschritt} status={detail.status} />
                      {maLieferterminSummary(detail) ? (
                        <span className="text-sm text-label-secondary">
                          LT {maLieferterminSummary(detail)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    loading={claiming}
                    onClick={() => void handleClaim()}
                  >
                    Bearbeitung starten
                  </Button>
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <FortschrittBadge f={fortschritt} status={detail.status} />
                  </div>
                  {detail.status === "abgelehnt" && detail.storno_grund ? (
                    <div className="rounded-lg border border-status-red/30 bg-status-red/10 px-3 py-2 text-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-status-red">
                        Storniert
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-label">{detail.storno_grund}</p>
                      {detail.storniert_at ? (
                        <p className="mt-1 text-xs text-label-secondary">
                          {fmtMaDateTime(detail.storniert_at)}
                          {detail.storniert_von ? ` · ${detail.storniert_von}` : ""}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </section>

            <section className="min-w-0 rounded-ios border border-separator bg-card p-3">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-label">Zusammenfassung</h3>
                {(isBuero || isAdmin) ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {canPrintMaSheet ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={printLoading}
                        disabled={!canPrintMaSheet || printLoading}
                        title="PDF mit QR-Code: Monteur bestätigt „Im Lager bereitgelegt“"
                        onClick={() => void handlePrintMaSheet()}
                      >
                        Materialanforderung drucken
                      </Button>
                    ) : null}
                    {positionSummary.total > 0 ? (
                      <>
                        <label className="flex items-center gap-1.5 text-xs text-label-secondary">
                          <input
                            type="checkbox"
                            className="rounded border-separator"
                            checked={printOnly === "all"}
                            onChange={(e) =>
                              setPrintOnly(e.target.checked ? "all" : "bereitgelegt")
                            }
                          />
                          Alle Positionen
                        </label>
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={printLoading}
                          disabled={!canPrintSummary || printLoading}
                          title={
                            canPrintSummary
                              ? "Lager-Zusammenfassung als PDF öffnen"
                              : detail.bearbeitet_von
                                ? "Zuerst Positionen als „Im Lager bereitgelegt“ markieren"
                                : "Zuerst die Bearbeitung übernehmen"
                          }
                          onClick={() => void handlePrintSummary()}
                        >
                          Zusammenfassung drucken
                        </Button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs text-label-secondary">Auftrag</dt>
                  <dd className="font-medium text-label">{detail.auftrag_nummer || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-label-secondary">Angefordert von</dt>
                  <dd className="text-label">
                    {detail.created_by} · {fmtMaDateTime(detail.created_at)}
                  </dd>
                </div>
              </dl>
              {detail.notes ? (
                <div className="mt-3 min-w-0 rounded-lg bg-fill-tertiary px-3 py-2 text-sm text-label">
                  <p className="text-xs font-medium text-label-secondary">Nachricht vom Monteur</p>
                  <p className="mt-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                    {detail.notes}
                  </p>
                </div>
              ) : null}
            </section>

            <section className="min-w-0 rounded-ios border border-separator bg-card p-3">
              <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-label">
                  Positionen ({detail.positionen?.length ?? 0})
                </h3>
                {detail.positionen?.length ? (
                  <p className="min-w-0 text-xs text-label-secondary">
                    {formatMaPositionOrderSummary(detail.positionen) || "—"}
                  </p>
                ) : null}
              </div>
              {detail.positionen?.length ? (
                <ul className="mt-2 min-w-0 divide-y divide-separator text-sm">
                  {detail.positionen.map((p, idx) => {
                    const state = maPositionOrderState(p);
                    const closedPos =
                      state === "storniert" || state === "abgelehnt";
                    const canDisposeRow =
                      canActOnMa &&
                      !closedPos &&
                      state !== "bestellt" &&
                      state !== "geliefert" &&
                      p.id != null;
                    const canLinkRow =
                      (isBuero || isAdmin) &&
                      !closedPos &&
                      (state === "offen" || state === "entwurf") &&
                      p.id != null;
                    const canClearRow =
                      canActOnMa && closedPos && p.id != null;
                    return (
                    <li key={p.id} className="flex min-w-0 gap-3 py-2">
                      <span className="shrink-0 font-mono text-xs text-label-tertiary">
                        {idx + 1}.
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-start justify-between gap-2">
                          <p className="min-w-0 flex-1 break-words font-medium text-label [overflow-wrap:anywhere]">
                            {p.menge != null ? `${p.menge} ` : ""}
                            {p.einheit ? `${p.einheit} ` : ""}
                            {p.text}
                          </p>
                          <span className="shrink-0">
                            <MaPositionOrderBadge position={p} />
                          </span>
                        </div>
                        {p.notes ? (
                          <p className="break-words text-xs text-label-secondary [overflow-wrap:anywhere]">
                            {p.notes}
                          </p>
                        ) : null}
                        {p.hero_nr ? (
                          <p className="text-xs text-accent">Hero {p.hero_nr}</p>
                        ) : null}
                        {canDisposeRow || canClearRow || canLinkRow ? (
                          <div className="mt-1.5 flex flex-wrap gap-2">
                            {canLinkRow ? (
                              <button
                                type="button"
                                className="text-xs text-accent hover:underline"
                                disabled={dispositionLoading || !!busy}
                                onClick={() =>
                                  openLinkOrderDialog(null, [p.id])
                                }
                              >
                                Mit Bestellung verknüpfen
                              </button>
                            ) : null}
                            {canDisposeRow ? (
                              <>
                                <button
                                  type="button"
                                  className="text-xs text-label-secondary hover:underline"
                                  disabled={dispositionLoading || !!busy}
                                  onClick={() =>
                                    openDispositionDialog("storniert", [p.id])
                                  }
                                >
                                  Stornieren
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-status-red hover:underline"
                                  disabled={dispositionLoading || !!busy}
                                  onClick={() =>
                                    openDispositionDialog("abgelehnt", [p.id])
                                  }
                                >
                                  Ablehnen
                                </button>
                              </>
                            ) : null}
                            {canClearRow ? (
                              <button
                                type="button"
                                className="text-xs text-accent hover:underline"
                                disabled={dispositionLoading || !!busy}
                                onClick={() => void handleClearDisposition(p.id)}
                              >
                                Status zurücknehmen
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-label-tertiary">
                  Keine Positionen – nur Freitext/Anhänge.
                </p>
              )}
            </section>

            {storedAttachments.length > 0 ? (
              <section className="rounded-ios border border-separator bg-card p-3">
                <h3 className="mb-2 text-sm font-semibold text-label">Anhänge</h3>
                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {storedAttachments.map((a) => {
                    const isImg = (a.content_type || "").startsWith("image/");
                    const fullUrl = getMaAttachmentUrl(detail.id, a.id);
                    const thumbUrl = getMaAttachmentThumbUrl(detail.id, a.id);
                    return (
                      <li
                        key={a.id}
                        className="overflow-hidden rounded-ios border border-separator"
                      >
                        {isImg ? (
                          <button
                            type="button"
                            onClick={() => {
                              setLightboxUrl(thumbUrl);
                              setLightboxCopySource({
                                url: thumbUrl,
                                filename: a.filename,
                                contentType: a.content_type,
                              });
                            }}
                            onContextMenu={(e) =>
                              openMediaContextMenu(e, {
                                url: fullUrl,
                                filename: a.filename,
                                contentType: a.content_type,
                                isImage: true,
                                imageElement: imageElementFromContextEvent(e.target, e.currentTarget),
                              })
                            }
                            className="block h-24 w-full bg-fill-tertiary"
                          >
                            <img
                              src={thumbUrl}
                              alt={a.filename}
                              crossOrigin="use-credentials"
                              className="h-full w-full object-cover"
                            />
                          </button>
                        ) : (
                          <a
                            href={fullUrl}
                            target="_blank"
                            rel="noopener"
                            onContextMenu={(e) =>
                              openMediaContextMenu(e, {
                                url: fullUrl,
                                filename: a.filename,
                                contentType: a.content_type,
                                isImage: false,
                              })
                            }
                            className="block px-2 py-4 text-center text-xs text-label"
                          >
                            {a.filename}
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {showLinkedSection ? (
              <section className="rounded-ios border border-separator bg-card-elevated p-3 text-sm">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-label">Umgewandelt in</h3>
                  {(isBuero || isAdmin) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!!busy || bereitgelegtLoading}
                      onClick={() => openLinkOrderDialog(null)}
                    >
                      Bestellung verknüpfen
                    </Button>
                  ) : null}
                </div>
                {hasLinkedItems ? (
                <ul className="space-y-2">
                  {linkedAga ? (
                    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                        Angebotsanfrage
                      </span>
                      <a
                        href={`/angebotsanfragen?id=${linkedAga.aga_id}`}
                        className="text-accent hover:underline"
                      >
                        {linkedAga.aga_nummer}
                      </a>
                    </li>
                  ) : null}
                  {detail.linked_anfragen.map((l) => (
                    <li key={`an-${l.anfrage_id}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                        Anfrage (TA)
                      </span>
                      <a
                        href={`/anfragen?id=${l.anfrage_id}`}
                        className="text-accent hover:underline"
                      >
                        {l.ta_subject || `#${l.anfrage_id}`}
                      </a>
                      {l.supplier_name ? (
                        <span className="text-xs text-label-tertiary">· {l.supplier_name}</span>
                      ) : null}
                    </li>
                  ))}
                  {orderDrafts.map((d) => (
                    <li
                      key={`draft-${d.draft_id}`}
                      className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
                    >
                      <span className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                        TK
                      </span>
                      <button
                        type="button"
                        className="text-accent hover:underline"
                        onClick={() =>
                          void openOrderDraftById(d.draft_id, `Bestellung aus ${detail.ma_nummer}`)
                        }
                      >
                        Entwurf #{d.draft_id}
                        {d.supplier_name ? ` · ${d.supplier_name}` : ""}
                      </button>
                      <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-secondary">
                        Entwurf
                      </span>
                    </li>
                  ))}
                  {detail.linked_orders.map((l) => {
                    const orderLabel =
                      l.bestellnummer || l.subject || `Bestellung #${l.order_id}`;
                    return (
                    <li
                      key={`ord-${l.order_id}`}
                      className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
                    >
                      <span className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                        TK
                      </span>
                      <a
                        href={`/bestellungen?id=${l.order_id}`}
                        className={
                          l.cancelled_at
                            ? "eh-order-tk-badge eh-order-tk-badge--cancelled font-mono text-xs font-semibold hover:underline"
                            : "text-accent hover:underline"
                        }
                      >
                        {orderLabel}
                      </a>
                      {l.supplier_name ? (
                        <span className="text-xs text-label-tertiary">· {l.supplier_name}</span>
                      ) : null}
                      {l.geliefert_at ? (
                        <span className="rounded-full bg-status-green/15 px-2 py-0.5 text-xs text-status-green">
                          Geliefert
                        </span>
                      ) : l.cancelled_at ? (
                        <span className="text-xs text-status-red">· storniert</span>
                      ) : null}
                      {(isBuero || isAdmin) ? (
                        <span className="ml-auto flex flex-wrap items-center gap-1">
                          {!l.cancelled_at && remainingOpenIds.length > 0 ? (
                            <>
                              <button
                                type="button"
                                className="text-xs text-accent hover:underline disabled:opacity-50"
                                disabled={!!busy || bereitgelegtLoading}
                                onClick={() =>
                                  void handleAttachOpenPositions(
                                    l.order_id,
                                    orderLabel,
                                  )
                                }
                              >
                                Offene Positionen verknüpfen
                              </button>
                              <span className="text-xs text-label-tertiary">·</span>
                            </>
                          ) : null}
                          <button
                            type="button"
                            className="text-xs text-accent hover:underline disabled:opacity-50"
                            disabled={!!busy || bereitgelegtLoading}
                            onClick={() => openLinkOrderDialog(l.order_id)}
                          >
                            Ändern
                          </button>
                          <span className="text-xs text-label-tertiary">·</span>
                          <button
                            type="button"
                            className="text-xs text-status-red hover:underline disabled:opacity-50"
                            disabled={!!busy || bereitgelegtLoading}
                            onClick={() =>
                              void handleUnlinkOrder(l.order_id, orderLabel)
                            }
                          >
                            Trennen
                          </button>
                        </span>
                      ) : null}
                    </li>
                    );
                  })}
                </ul>
                ) : (
                  <p className="text-xs text-label-secondary">
                    Noch keine verknüpften Dokumente. Bestehende Bestellungen können nachträglich
                    zugeordnet werden.
                  </p>
                )}
              </section>
            ) : null}

            <section className="rounded-ios border border-accent/30 bg-accent/5 p-3">
              <label className="eh-label" htmlFor="ma-buero-notizen">
                Bürointerne Notizen
              </label>
              <p className="mb-2 text-xs text-label-secondary">
                Nur für das Büro sichtbar – jede gespeicherte Notiz erscheint in den Projektnotizen
                mit Kennzeichnung „Materialanforderung“.
              </p>
              <textarea
                id="ma-buero-notizen"
                className="eh-input min-h-[5rem]"
                rows={4}
                value={bueroNotizen}
                disabled={!canEditBueroNotizen}
                onChange={(e) => setBueroNotizen(e.target.value)}
                placeholder="Neue interne Notiz …"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={saving}
                  disabled={!canEditBueroNotizen}
                  onClick={() => void saveBueroNotizen()}
                >
                  Notiz speichern
                </Button>
              </div>
            </section>
          </div>
        </div>

        <footer
          className="ma-dialog-footer ma-dialog-footer--compact flex min-w-0 shrink-0 flex-wrap items-center gap-2 border-t border-separator bg-card-elevated px-5 py-3 max-sm:gap-1.5 max-sm:px-3 max-sm:py-2 max-sm:pb-[max(0.5rem,env(safe-area-inset-bottom))]"
        >
          <Button variant="ghost" size="sm" onClick={onClose} className="shrink-0">
            Schließen
          </Button>
          <div className="flex min-w-0 flex-1 flex-wrap justify-end gap-2">
            {canPrintMaSheet ? (
              <Button
                variant="secondary"
                size="sm"
                loading={printLoading}
                disabled={!canPrintMaSheet || printLoading || !!busy}
                title="PDF mit QR-Code: Monteur bestätigt „Im Lager bereitgelegt“"
                onClick={() => void handlePrintMaSheet()}
              >
                Materialanforderung drucken
              </Button>
            ) : null}
            {(isBuero || isAdmin) && positionSummary.total > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                loading={printLoading}
                disabled={!canPrintSummary || printLoading || !!busy}
                title={
                  canPrintSummary
                    ? printOnly === "all"
                      ? "Alle Positionen als PDF drucken"
                      : "Bereitgelegte Positionen als PDF drucken"
                    : detail.bearbeitet_von
                      ? "Zuerst Positionen als „Im Lager bereitgelegt“ markieren"
                      : "Zuerst die Bearbeitung übernehmen"
                }
                onClick={() => void handlePrintSummary()}
              >
                Zusammenfassung drucken
              </Button>
            ) : null}
            {canActOnMa && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={busy === "anfrage"}
                  disabled={!!busy || bereitgelegtLoading}
                  onClick={() => void handleAnfrage()}
                >
                  In Anfrage (TA)
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={busy === "order"}
                  disabled={!!busy || bereitgelegtLoading}
                  onClick={() => setOrderSupplierOpen(true)}
                >
                  In Bestellung (TK)
                </Button>
                {canMarkBereitgelegt ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={bereitgelegtLoading}
                    disabled={!!busy || dispositionLoading}
                    onClick={() => openBereitgelegtDialog()}
                  >
                    Im Lager bereitgelegt
                  </Button>
                ) : null}
                {canDisposePositions ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!!busy || bereitgelegtLoading || dispositionLoading}
                      onClick={() => openDispositionDialog("storniert")}
                    >
                      Pos. stornieren
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!!busy || bereitgelegtLoading || dispositionLoading}
                      onClick={() => openDispositionDialog("abgelehnt")}
                    >
                      Pos. ablehnen
                    </Button>
                  </>
                ) : null}
                <Button
                  variant="danger"
                  size="sm"
                  disabled={!!busy || bereitgelegtLoading || dispositionLoading}
                  onClick={() => setStornoOpen(true)}
                >
                  Stornieren
                </Button>
              </>
            )}
            {canUnmarkBereitgelegt ? (
              <Button
                variant="ghost"
                size="sm"
                loading={bereitgelegtLoading}
                disabled={!!busy}
                onClick={() => void handleUnmarkBereitgelegt()}
              >
                Bereitlegung zurücknehmen
              </Button>
            ) : null}
          </div>
        </footer>
      </div>

      {showProjectNotes ? (
        <aside
          className="flex min-h-0 w-full shrink-0 flex-col border-t border-separator bg-card lg:w-[min(22rem,34%)] lg:border-l lg:border-t-0"
        >
          <div className="min-h-0 flex-1 overflow-hidden p-3">
            <NotesSection
              orderId={detail.auftrag_id!}
              project={project}
              notes={projectNotes}
              user={user}
              isBuero={isBuero}
              allowCompose={canActOnMa}
              onChanged={() => void reloadProjectNotes()}
            />
          </div>
        </aside>
      ) : null}

      <Lightbox
        url={lightboxUrl}
        copySource={lightboxCopySource}
        onClose={() => {
          setLightboxUrl(null);
          setLightboxCopySource(null);
        }}
      />
      {contextMenuNode}

      <MaOrderSupplierDialog
        open={orderSupplierOpen}
        maNummer={detail.ma_nummer}
        positionen={detail.positionen || []}
        loading={busy === "order"}
        onClose={() => {
          if (busy !== "order") setOrderSupplierOpen(false);
        }}
        onConfirm={(result) => void handleOrderConfirm(result)}
      />

      <MaBereitgelegtDialog
        open={bereitgelegtOpen}
        maNummer={detail.ma_nummer}
        positionen={detail.positionen || []}
        loading={bereitgelegtLoading}
        onClose={() => {
          if (!bereitgelegtLoading) setBereitgelegtOpen(false);
        }}
        onConfirm={(positionIds) => void handleMarkBereitgelegtConfirm(positionIds)}
      />

      <MaPositionDispositionDialog
        open={dispositionOpen}
        mode={dispositionMode}
        maNummer={detail.ma_nummer}
        positionen={detail.positionen || []}
        initialPositionIds={dispositionInitialIds}
        loading={dispositionLoading}
        onClose={() => {
          if (!dispositionLoading) setDispositionOpen(false);
        }}
        onConfirm={(positionIds, grund) =>
          void handleDispositionConfirm(positionIds, grund)
        }
      />

      <MaLinkOrderDialog
        open={linkOrderOpen}
        maId={detail.id}
        maNummer={detail.ma_nummer}
        auftragId={detail.auftrag_id}
        replaceOrderId={replaceOrderId}
        includeSameMaOrders={replaceOrderId == null && remainingOpenIds.length > 0}
        positionHint={
          linkPositionIds?.length === 1
            ? (detail.positionen || []).find((p) => p.id === linkPositionIds[0])
                ?.text || null
            : null
        }
        loading={busy === "link"}
        onClose={() => {
          if (busy !== "link") {
            setLinkOrderOpen(false);
            setReplaceOrderId(null);
            setLinkPositionIds(null);
          }
        }}
        onConfirm={(orderId) => void handleLinkOrderConfirm(orderId)}
      />

      <MaStornoDialog
        open={stornoOpen}
        maNummer={detail.ma_nummer}
        loading={stornoLoading}
        onClose={() => {
          if (!stornoLoading) setStornoOpen(false);
        }}
        onConfirm={(grund) => void handleStorno(grund)}
      />

      <MaBearbeitungTransferDialog
        open={transferOpen}
        mode={transferMode}
        maNummer={detail.ma_nummer}
        loading={transferLoading}
        currentAssigneeName={bearbeiterName}
        onClose={() => {
          if (!transferLoading) setTransferOpen(false);
        }}
        onConfirmRelease={(grund) => void handleRelease(grund)}
        onConfirmAssign={(assigneeLogin, grund) => void handleAssign(assigneeLogin, grund)}
      />
    </div>
  );
}
